<template>
    <div class="tl-scroll">
        <div class="tl" :class="{ paint }" :style="{ minWidth: minWidth }">
            <div class="tl-head" :style="{ gridTemplateColumns: columns }">
                <div class="tl-corner caption grey--text">{{ staff.length }} staff</div>
                <div v-for="d in days" :key="d.date" class="tl-day" :class="{ off: d.off }" :title="d.holiday">
                    <div class="tl-dow">{{ d.dow }}</div>
                    <div class="tl-num" :class="{ 'primary white--text': d.today }">{{ d.num }}</div>
                </div>
            </div>

            <div class="tl-body">
                <div class="tl-cols" :style="{ gridTemplateColumns: columns }">
                    <div></div>
                    <div v-for="d in days" :key="d.date" class="tl-col" :class="{ off: d.off, today: d.today }"></div>
                </div>

                <div v-if="!staff.length" class="tl-empty caption grey--text">No staff match the filters.</div>

                <template v-for="g in groups">
                    <div :key="'g' + g.key" class="tl-group" @click="toggle(g.key)">
                        <span class="tl-group-label">
                            <v-icon small>{{ collapsed[g.key] ? 'mdi-chevron-right' : 'mdi-chevron-down' }}</v-icon>
                            {{ g.name }}
                            <span class="grey--text font-weight-regular ml-1">· {{ g.rows.length }}</span>
                        </span>
                    </div>
                    <template v-if="!collapsed[g.key]">
                        <div
                            v-for="r in g.rows"
                            :key="r.staff_id"
                            class="tl-row"
                            :style="{ gridTemplateColumns: columns, gridTemplateRows: `repeat(${r.lanes}, ${laneHeight}px)` }"
                            @mousedown="paint && dragStart($event, r)"
                            @mousemove="drag && dragMove($event, r)"
                        >
                            <div class="tl-staff" :style="{ gridColumn: 1, gridRow: `1 / span ${r.lanes}` }" :title="staffTitle(r)"
                                @click="paint && $emit('staff-click', r)">
                                <v-avatar size="22" color="grey lighten-3" class="mr-2 flex-shrink-0">
                                    <img :src="r.profile_url" alt="" loading="lazy">
                                </v-avatar>
                                <span class="tl-empno">{{ r.empno }}</span>
                                <span class="tl-name">{{ r.name }}</span>
                                <span v-if="badges[r.staff_id]" class="tl-badge" :class="{ warn: badges[r.staff_id].warn }"
                                    :title="badges[r.staff_id].title">{{ badges[r.staff_id].text }}</span>
                            </div>
                            <div
                                v-for="b in r.bars"
                                :key="b.key"
                                class="tl-bar"
                                :class="b.classes"
                                :style="b.style"
                                :title="b.title"
                                @click.stop="paint && $emit('entry-click', b.entry)"
                            ><span class="tl-bar-text">{{ b.text }}</span></div>
                            <div v-for="c in pendingCells(r)" :key="'p' + c.date" class="tl-pending" :style="c.style" :title="c.title"></div>
                            <div v-if="drag && drag.row.staff_id === r.staff_id" class="tl-drag"
                                :style="{ gridColumn: `${Math.min(drag.start, drag.end) + 2} / ${Math.max(drag.start, drag.end) + 3}`, gridRow: `1 / span ${r.lanes}` }"></div>
                        </div>
                    </template>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
// Team timeline ("who's out" wallchart) for the shared calendar: one row per
// staff member grouped by section, one column per day of the month, a bar per
// entry. Plans are striped, not-approved entries are a light wash of the type
// colour. SharedCalendar does the filtering; this only lays the bars out.
// `paint` (HR's planner): dragging across a row's days emits paint (staff
// row, first and last day), a click on a bar entry-click, on a name
// staff-click; `pending` (staff_id → [{date, type}]) draws days about to be
// added, `removing` (entry id → true) the entries about to be removed.
// `badges` (staff_id → {text, title, warn}): a note beside the name.
const NAME_WIDTH = 230
const MIN_DAY_WIDTH = 28

export default {
    name: 'TeamTimeline',
    props: {
        year: { type: Number, required: true },
        month: { type: Number, required: true },
        staff: { type: Array, required: true },
        entries: { type: Array, required: true },
        typeByKey: { type: Object, required: true },
        holidays: { type: Array, default: () => [] },
        titleFor: { type: Function, required: true },
        paint: { type: Boolean, default: false },
        pending: { type: Object, default: () => ({}) },
        removing: { type: Object, default: () => ({}) },
        badges: { type: Object, default: () => ({}) },
    },
    data: () => ({
        collapsed: {},
        laneHeight: 26,
        drag: null, // { row, start, end } day indexes while painting
    }),
    computed: {
        days() {
            const names = {}
            this.holidays.forEach(h => { names[h.date] = h.name })
            const today = this.ymd(new Date())
            const count = new Date(this.year, this.month, 0).getDate()
            return Array.from({ length: count }, (_, i) => {
                const d = new Date(this.year, this.month - 1, i + 1)
                const date = this.ymd(d)
                const weekend = d.getDay() === 5 || d.getDay() === 6
                return {
                    date,
                    num: i + 1,
                    dow: 'SMTWTFS'[d.getDay()],
                    off: weekend || date in names, // PublicHolidays holds every Fri/Sat too, once HR has entered the year
                    holiday: date in names && !weekend ? names[date] : '',
                    today: date === today,
                }
            })
        },
        columns() {
            return `${NAME_WIDTH}px repeat(${this.days.length}, minmax(${MIN_DAY_WIDTH}px, 1fr))`
        },
        minWidth() {
            return `${NAME_WIDTH + this.days.length * MIN_DAY_WIDTH}px`
        },
        groups() {
            const byStaff = {}
            this.entries.forEach(e => { (byStaff[e.staff_id] = byStaff[e.staff_id] || []).push(e) })
            const groups = []
            let g = null
            this.staff.forEach(s => {
                if (!g || g.key !== s.section_id) {
                    g = { key: s.section_id, name: s.section || '—', rows: [] }
                    groups.push(g)
                }
                g.rows.push({ ...s, ...this.layout(byStaff[s.staff_id] || []) })
            })
            return groups
        },
    },
    beforeDestroy() {
        window.removeEventListener('mouseup', this.dragEnd)
    },
    methods: {
        // Bars clipped to the month, stacked into lanes where they overlap.
        layout(list) {
            const first = this.days[0].date
            const last = this.days[this.days.length - 1].date
            const items = list
                .map(e => ({
                    e,
                    start: this.dayIndex(e.from_date < first ? first : e.from_date),
                    end: this.dayIndex(e.to_date > last ? last : e.to_date),
                }))
                .sort((a, b) => a.start - b.start || Number(a.e.planned) - Number(b.e.planned))
            const laneEnds = []
            items.forEach(it => {
                let lane = laneEnds.findIndex(end => end < it.start)
                if (lane === -1) {
                    lane = laneEnds.length
                    laneEnds.push(it.end)
                } else {
                    laneEnds[lane] = it.end
                }
                it.lane = lane
            })
            return {
                lanes: Math.max(1, laneEnds.length),
                bars: items.map(it => this.bar(it, first, last)),
            }
        },
        bar(it, first, last) {
            const e = it.e
            const t = this.typeByKey[e.type] || { label: e.type, color: '#757575' }
            const span = it.end - it.start + 1
            return {
                key: `${e.source}-${e.id}-${it.start}`,
                entry: e,
                text: span === 1 ? this.abbr(t.label) : (e.planned ? `Planned · ${t.label}` : t.label),
                title: this.titleFor(e),
                classes: {
                    planned: e.planned,
                    removing: e.source === 'schedule' && !!this.removing[e.id],
                    narrow: span === 1,
                    'cont-left': e.from_date < first,
                    'cont-right': e.to_date > last,
                },
                style: {
                    gridColumn: `${it.start + 2} / ${it.end + 3}`,
                    gridRow: it.lane + 1,
                    backgroundColor: e.approved ? t.color : this.tint(t.color),
                    color: e.approved ? '#ffffff' : t.color,
                },
            }
        },
        // "Work From Home" → WFH, "Maternity Leave [6 months]" → ML.
        abbr(label) {
            return String(label || '').replace(/[([].*$/, '').trim().split(/\s+/).map(w => w[0]).join('').toUpperCase()
        },
        tint(color) {
            return /^#[0-9a-f]{6}$/i.test(color) ? `${color}2e` : '#eeeeee'
        },
        dayIndex(date) {
            return Number(date.slice(8, 10)) - 1
        },
        ymd(d) {
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        },
        staffTitle(s) {
            const unit = s.unit && s.unit !== '-' ? ` / ${s.unit}` : ''
            return `${s.empno} ${s.name}\n${s.section}${unit}`
        },
        toggle(key) {
            this.$set(this.collapsed, key, !this.collapsed[key])
        },
        /** Index of the day under the pointer in a row (event.currentTarget). */
        dayAt(event) {
            const rect = event.currentTarget.getBoundingClientRect()
            const i = Math.floor((event.clientX - rect.left - NAME_WIDTH) / ((rect.width - NAME_WIDTH) / this.days.length))
            return Math.min(Math.max(i, 0), this.days.length - 1)
        },
        dragStart(event, row) {
            if (event.button !== 0 || event.target.closest('.tl-staff, .tl-bar')) return
            event.preventDefault() // no text selection while dragging
            const i = this.dayAt(event)
            this.drag = { row, start: i, end: i }
            window.addEventListener('mouseup', this.dragEnd, { once: true })
        },
        dragMove(event, row) {
            if (this.drag.row.staff_id === row.staff_id) this.drag.end = this.dayAt(event)
        },
        dragEnd() {
            const d = this.drag
            this.drag = null
            if (!d) return
            this.$emit('paint', d.row, this.days[Math.min(d.start, d.end)].date, this.days[Math.max(d.start, d.end)].date)
        },
        pendingCells(row) {
            const prefix = this.days[0].date.slice(0, 7)
            return (this.pending[row.staff_id] || []).filter(p => p.date.startsWith(prefix)).map(p => {
                const t = this.typeByKey[p.type] || { label: p.type, color: '#757575' }
                return {
                    date: p.date,
                    title: `${t.label} — to be added`,
                    style: { gridColumn: this.dayIndex(p.date) + 2, gridRow: `1 / span ${row.lanes}`, borderColor: t.color, backgroundColor: this.tint(t.color) },
                }
            })
        },
    },
}
</script>

<style scoped>
.tl-scroll {
    overflow: auto;
    max-height: calc(100vh - 290px);
    min-height: 320px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
}
.tl {
    position: relative;
}
.tl-head {
    display: grid;
    position: sticky;
    top: 0;
    z-index: 4;
    background: #fff;
    border-bottom: 1px solid #e0e0e0;
}
.tl-corner {
    position: sticky;
    left: 0;
    z-index: 5;
    display: flex;
    align-items: flex-end;
    padding: 0 12px 6px;
    background: #fff;
}
.tl-day {
    text-align: center;
    padding: 4px 0;
    font-size: 11px;
    line-height: 16px;
}
.tl-day.off {
    background: #f6f6f6;
}
.tl-dow {
    color: #9e9e9e;
}
.tl-num {
    display: inline-block;
    min-width: 20px;
    border-radius: 10px;
    font-weight: 600;
}
.tl-body {
    position: relative;
}
.tl-cols {
    display: grid;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    pointer-events: none;
}
.tl-col {
    border-left: 1px solid #f2f2f2;
}
.tl-col.off {
    background: #f6f6f6;
}
.tl-col.today {
    background: rgba(25, 118, 210, 0.07);
}
.tl-empty {
    position: relative;
    padding: 24px;
    text-align: center;
}
.tl-group {
    position: relative;
    z-index: 2;
    background: #fafafa;
    border-bottom: 1px solid #eeeeee;
    cursor: pointer;
    user-select: none;
}
.tl-group-label {
    position: sticky;
    left: 0;
    display: inline-block;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 600;
}
.tl-row {
    display: grid;
    position: relative;
    border-bottom: 1px solid #f2f2f2;
}
.tl-row:hover {
    background: rgba(0, 0, 0, 0.025);
}
.tl-staff {
    position: sticky;
    left: 0;
    z-index: 3;
    display: flex;
    align-items: center;
    padding: 0 8px 0 12px;
    background: #fff;
    border-right: 1px solid #eeeeee;
    font-size: 12px;
    overflow: hidden;
}
.tl-row:hover .tl-staff {
    background: #f9f9f9;
}
.tl-empno {
    font-weight: 600;
    margin-right: 6px;
    flex-shrink: 0;
}
.tl-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.tl-badge {
    flex-shrink: 0;
    margin-left: auto;
    padding-left: 6px;
    font-size: 11px;
    color: #757575;
}
.tl-badge.warn {
    color: #e53935;
    font-weight: 600;
}
.tl-bar {
    position: relative;
    z-index: 1;
    align-self: center;
    height: 20px;
    margin: 0 1px;
    padding: 0 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    line-height: 20px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.tl-bar.narrow {
    padding: 0 1px;
    text-align: center;
    font-size: 10px;
}
.tl-bar.cont-left {
    margin-left: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}
.tl-bar.cont-right {
    margin-right: 0;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}
.tl-bar.planned::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: repeating-linear-gradient(135deg, currentColor 0 2px, transparent 2px 6px);
    opacity: 0.25;
    pointer-events: none;
}
.tl-bar-text {
    position: relative;
}
.tl.paint .tl-row {
    cursor: cell;
    user-select: none;
}
.tl.paint .tl-staff,
.tl.paint .tl-bar {
    cursor: pointer;
}
.tl-bar.removing {
    opacity: 0.45;
    text-decoration: line-through;
    outline: 2px solid #e53935;
}
.tl-pending {
    position: relative;
    z-index: 1;
    margin: 2px 1px;
    border: 2px dashed;
    border-radius: 4px;
    pointer-events: none;
}
.tl-drag {
    position: relative;
    z-index: 2;
    background: rgba(25, 118, 210, 0.15);
    border: 1px solid rgba(25, 118, 210, 0.5);
    border-radius: 4px;
    pointer-events: none;
}
</style>
